import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AlertTriangle, Shield, Zap, Brain, Clock, Users, ChevronRight,
  CheckCircle, Loader2, MessageCircle, ArrowRight, Send, FileText, Heart, Activity
} from 'lucide-react';

const PRIORITY_STYLE = {
  urgent: 'bg-rose-500/10 border-rose-500/15 text-rose-400',
  high: 'bg-amber-500/10 border-amber-500/15 text-amber-400',
  normal: 'bg-blue-500/10 border-blue-500/15 text-blue-400',
  low: 'bg-zinc-500/10 border-zinc-500/15 text-zinc-400',
};

// ── Read-only highlight-on-arrival for /admin/care-flow?task_id=... ──
// When the URL carries a task_id, the matching TaskCard scrolls into view
// and shows a soft amber ring for 3s. No mutations, no clinical changes.
function useTaskHighlight(allTaskIds) {
  const [searchParams] = useSearchParams();
  const wantedId = searchParams.get('task_id');
  const [activeId, setActiveId] = useState(null);
  const fired = useRef(false);

  useEffect(() => {
    if (!wantedId || fired.current) return;
    if (!allTaskIds || allTaskIds.length === 0) return;
    if (!allTaskIds.includes(wantedId)) return;  // missing task → graceful no-op
    fired.current = true;
    setActiveId(wantedId);
    // Defer scroll until card is mounted
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="careflow-task-row-${wantedId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    // NOTE: intentionally no cleanup. We want the fade to fire even if the
    // parent re-renders (e.g. background data refresh changes allTaskIds
    // identity). Re-firing is guarded by `fired.current`.
    setTimeout(() => setActiveId(null), 3000);
  }, [wantedId, allTaskIds]);

  return activeId;
}

function TaskCard({ task, role, onAction, highlightedId }) {
  const style = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.normal;
  const roleLabels = { admin: 'Admin Decision', counselor: 'Counselor', health_coach: 'Health Coach' };
  const isHighlighted = highlightedId && highlightedId === task.id;

  return (
    <div
      data-testid={`careflow-task-row-${task.id}`}
      className={`transition-all duration-700 ${isHighlighted
        ? 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-adm-bg rounded-lg shadow-[0_0_24px_rgba(245,158,11,0.35)]'
        : ''}`}
    >
      {isHighlighted && (
        <div data-testid="careflow-highlighted-task" className="sr-only">
          highlighted task: {task.id}
        </div>
      )}
      <Card className={`border ${style.split(' ')[1]} bg-adm-surface`} data-testid={`task-${task.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`text-[8px] border-0 ${style}`}>{task.priority}</Badge>
            <Badge className="text-[8px] bg-white/5 text-adm-tertiary border-0">{task.task_type?.replace(/_/g, ' ')}</Badge>
          </div>
          <span className="text-[9px] text-adm-tertiary">{task.created_at ? new Date(task.created_at).toLocaleDateString() : ''}</span>
        </div>
        <h4 className="text-sm font-['Outfit'] font-medium text-adm-primary">{task.title}</h4>
        {task.subscriber_name && <span className="text-[10px] text-adm-accent">{task.subscriber_name}</span>}
        {task.description && <p className="text-xs text-adm-tertiary leading-relaxed">{task.description}</p>}
        {task.admin_decision && (
          <div className="bg-adm-accent/5 rounded-lg px-3 py-2 border border-adm-accent/10">
            <span className="text-[9px] text-adm-accent uppercase tracking-wider">Admin Decision</span>
            <p className="text-xs text-adm-secondary mt-0.5">{task.admin_decision}</p>
          </div>
        )}

        {/* Role tag */}
        <div className="flex items-center gap-1 text-[9px] text-adm-tertiary">
          <Shield className="w-3 h-3" />
          {task.assigned_role ? roleLabels[task.assigned_role] || task.assigned_role : role}
        </div>

        {/* Actions */}
        <div className="flex gap-1.5 pt-1">
          {role === 'admin' && (
            <Button size="sm" className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 text-[10px] h-7 px-2" onClick={() => onAction('decide', task)} data-testid={`decide-${task.id}`}>
              <CheckCircle className="w-3 h-3 mr-1" /> Decide
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-adm-tertiary hover:text-adm-primary text-[10px] h-7 px-2" onClick={() => onAction('complete', task)}>
            <CheckCircle className="w-3 h-3 mr-1" /> Done
          </Button>
          <Button size="sm" variant="ghost" className="text-adm-tertiary hover:text-adm-primary text-[10px] h-7 px-2" onClick={() => onAction('escalate', task)}>
            <ArrowRight className="w-3 h-3 mr-1" /> Escalate
          </Button>
          <Button size="sm" variant="ghost" className="text-adm-tertiary hover:text-adm-primary text-[10px] h-7 px-2" onClick={() => onAction('notes', task)}>
            <FileText className="w-3 h-3 mr-1" /> Notes
          </Button>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ADMIN COMMAND CENTER
// ═══════════════════════════════════════════════════════════

export function AdminCommandCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [decision, setDecision] = useState('');
  const [instruction, setInstruction] = useState('');
  const [notes, setNotes] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    axios.get(`${API}/care-flow/admin/command-center`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Read-only highlight-on-arrival
  const allTaskIds = React.useMemo(() => {
    const ids = [];
    [...(data?.critical || []), ...(data?.pending || []), ...(data?.escalated || [])]
      .forEach(t => t?.id && ids.push(t.id));
    return ids;
  }, [data]);
  const highlightedId = useTaskHighlight(allTaskIds);

  const handleAction = async (action, task) => {
    if (action === 'decide') { setDialog({ type: 'decide', task }); setDecision(''); setInstruction(''); return; }
    if (action === 'notes') { setDialog({ type: 'notes', task }); setNotes(''); return; }
    if (action === 'complete') {
      await axios.post(`${API}/care-flow/tasks/${task.id}/complete`, { outcome: 'completed' });
      toast.success('Task completed'); load(); return;
    }
    if (action === 'escalate') {
      await axios.post(`${API}/care-flow/tasks/${task.id}/escalate`, { reason: 'Admin escalation' });
      toast.success('Task escalated'); load(); return;
    }
  };

  const submitDecision = async () => {
    if (!dialog?.task) return;
    await axios.post(`${API}/care-flow/admin/decide/${dialog.task.id}`, {
      decision, instruction_for_counselor: instruction, action_taken: 'reviewed',
    });
    toast.success(instruction ? 'Decision made + instruction sent to counselor' : 'Decision recorded');
    setDialog(null); load();
  };

  const submitNotes = async () => {
    if (!dialog?.task) return;
    await axios.post(`${API}/care-flow/tasks/${dialog.task.id}/add-notes`, { notes });
    toast.success('Notes added');
    setDialog(null); load();
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-5" data-testid="command-center">
        <div>
          <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Command Center</h1>
          <p className="text-xs text-adm-tertiary mt-1">Admin intervention queue — clinical decisions requiring immediate attention</p>
          <div className="flex items-center gap-3 mt-2 text-[8px] text-white/20">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Role: Admin</span>
            <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> Data: Care flow tasks, escalations, predictive signals</span>
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Actions: Decide, instruct counselor, escalate, complete</span>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Critical', value: data?.summary?.critical_count || 0, color: 'text-rose-400 bg-rose-500/10' },
            { label: 'Pending', value: data?.summary?.pending_count || 0, color: 'text-amber-400 bg-amber-500/10' },
            { label: 'Escalated', value: data?.summary?.escalated_count || 0, color: 'text-blue-400 bg-blue-500/10' },
          ].map(s => (
            <Card key={s.label} className="bg-adm-surface border-white/[0.06]">
              <CardContent className="py-3 px-3 flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${s.color.split(' ')[1]} flex items-center justify-center`}>
                  <span className={`text-sm font-bold ${s.color.split(' ')[0]}`}>{s.value}</span>
                </div>
                <span className="text-xs text-adm-tertiary">{s.label}</span>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Critical */}
        {(data?.critical || []).length > 0 && (
          <div><h3 className="text-[10px] text-rose-400 uppercase tracking-wider mb-2">Critical Decisions</h3>
            <div className="space-y-2">{data.critical.map(t => <TaskCard key={t.id} task={t} role="admin" onAction={handleAction} highlightedId={highlightedId} />)}</div>
          </div>
        )}

        {/* Pending */}
        {(data?.pending || []).length > 0 && (
          <div><h3 className="text-[10px] text-amber-400 uppercase tracking-wider mb-2">Pending Decisions</h3>
            <div className="space-y-2">{data.pending.map(t => <TaskCard key={t.id} task={t} role="admin" onAction={handleAction} highlightedId={highlightedId} />)}</div>
          </div>
        )}

        {/* Escalated */}
        {(data?.escalated || []).length > 0 && (
          <div><h3 className="text-[10px] text-blue-400 uppercase tracking-wider mb-2">Escalated</h3>
            <div className="space-y-2">{data.escalated.map(t => <TaskCard key={t.id} task={t} role="admin" onAction={handleAction} highlightedId={highlightedId} />)}</div>
          </div>
        )}

        {(data?.critical?.length === 0 && data?.pending?.length === 0 && data?.escalated?.length === 0) && (
          <div className="text-center py-12 text-adm-tertiary text-xs">No decisions pending. System is clear.</div>
        )}

        {/* Decision Dialog */}
        <Dialog open={dialog?.type === 'decide'} onOpenChange={() => setDialog(null)}>
          <DialogContent className="bg-adm-panel border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="text-adm-primary font-['Outfit']">Make Decision</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              {dialog?.task?.title && <p className="text-xs text-adm-secondary">{dialog.task.title}</p>}
              <div><label className="text-xs text-adm-secondary mb-1 block">Your Decision</label>
                <Input value={decision} onChange={e => setDecision(e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="What should be done?" data-testid="decision-input" />
              </div>
              <div><label className="text-xs text-adm-secondary mb-1 block">Instruction for Counselor (optional)</label>
                <Input value={instruction} onChange={e => setInstruction(e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="What should counselor communicate?" data-testid="instruction-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} className="text-adm-secondary text-xs">Cancel</Button>
              <Button onClick={submitDecision} className="bg-adm-accent text-adm-bg text-xs" data-testid="submit-decision">Submit Decision</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notes Dialog */}
        <Dialog open={dialog?.type === 'notes'} onOpenChange={() => setDialog(null)}>
          <DialogContent className="bg-adm-panel border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="text-adm-primary">Add Notes</DialogTitle></DialogHeader>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="bg-white/5 border-white/10 text-white h-8 text-xs" placeholder="Add notes..." />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} className="text-adm-secondary text-xs">Cancel</Button>
              <Button onClick={submitNotes} className="bg-adm-accent text-adm-bg text-xs">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════
// COUNSELOR FLOW
// ═══════════════════════════════════════════════════════════

export function CounselorFlow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { axios.get(`${API}/care-flow/counselor/flow`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const allTaskIds = React.useMemo(() => {
    const ids = [];
    [...(data?.admin_instructions || []), ...(data?.tasks || [])].forEach(t => t?.id && ids.push(t.id));
    return ids;
  }, [data]);
  const highlightedId = useTaskHighlight(allTaskIds);

  const handleAction = async (action, task) => {
    if (action === 'complete') { await axios.post(`${API}/care-flow/tasks/${task.id}/complete`, { outcome: 'completed' }); toast.success('Done'); load(); }
    else if (action === 'escalate') { await axios.post(`${API}/care-flow/tasks/${task.id}/escalate`, { reason: 'Needs admin review' }); toast.success('Escalated to admin'); load(); }
    else if (action === 'notes') { const n = prompt('Add notes:'); if (n) { await axios.post(`${API}/care-flow/tasks/${task.id}/add-notes`, { notes: n }); toast.success('Notes added'); } }
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-5" data-testid="counselor-flow">
        <div>
          <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Care Flow</h1>
          <p className="text-xs text-adm-tertiary mt-1">Counselor communication and follow-up orchestration</p>
          <div className="flex items-center gap-3 mt-2 text-[8px] text-white/20">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Role: Counselor</span>
            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Data: Admin instructions, client follow-ups, communication tasks</span>
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Actions: Complete, escalate, add notes</span>
          </div>
        </div>

        {(data?.admin_instructions || []).length > 0 && (
          <div><h3 className="text-[10px] text-adm-accent uppercase tracking-wider mb-2">Admin Instructions</h3>
            <div className="space-y-2">{data.admin_instructions.map(t => <TaskCard key={t.id} task={t} role="counselor" onAction={handleAction} highlightedId={highlightedId} />)}</div>
          </div>
        )}

        {(data?.tasks || []).length > 0 && (
          <div><h3 className="text-[10px] text-blue-400 uppercase tracking-wider mb-2">Your Tasks</h3>
            <div className="space-y-2">{data.tasks.map(t => <TaskCard key={t.id} task={t} role="counselor" onAction={handleAction} highlightedId={highlightedId} />)}</div>
          </div>
        )}

        {(data?.admin_instructions?.length === 0 && data?.tasks?.length === 0) && (
          <div className="text-center py-12 text-adm-tertiary text-xs">No pending tasks. All clear.</div>
        )}
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════
// ROLE-AWARE CARE FLOW SWITCH
// ───────────────────────────────────────────────────────────
// Renders AdminCommandCenter for admins and CounselorFlow for
// counselor/health_coach/doctor. This keeps the deep-link URL
// `/admin/care-flow?task_id=...` working uniformly for all roles
// — admin tasks (e.g. protocol_evolution_review) only ever land
// in the AdminCommandCenter buckets, never in counselor/coach
// queries (whose backend endpoints filter by their own
// TASK_ROUTING list and so cannot leak admin-only tasks).
// ═══════════════════════════════════════════════════════════

export function CareFlowRoleSwitch() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === 'admin') return <AdminCommandCenter />;
  return <CounselorFlow />;
}

// ═══════════════════════════════════════════════════════════
// HEALTH COACH EXECUTION FLOW
// ═══════════════════════════════════════════════════════════

export function CoachExecutionFlow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { axios.get(`${API}/care-flow/coach/flow`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const allTaskIds = React.useMemo(() => (data?.tasks || []).map(t => t?.id).filter(Boolean), [data]);
  const highlightedId = useTaskHighlight(allTaskIds);

  const handleAction = async (action, task) => {
    if (action === 'complete') { await axios.post(`${API}/care-flow/tasks/${task.id}/complete`, { outcome: 'completed' }); toast.success('Done'); load(); }
    else if (action === 'escalate') { await axios.post(`${API}/care-flow/tasks/${task.id}/escalate`, { reason: 'Needs counselor review' }); toast.success('Escalated'); load(); }
    else if (action === 'notes') { const n = prompt('Add update:'); if (n) { await axios.post(`${API}/care-flow/tasks/${task.id}/add-notes`, { notes: n }); toast.success('Added'); } }
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="w-5 h-5 text-adm-accent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-5" data-testid="coach-flow">
        <div>
          <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Execution Flow</h1>
          <p className="text-xs text-adm-tertiary mt-1">Coach adherence follow-up, outreach, and task completion</p>
          <div className="flex items-center gap-3 mt-2 text-[8px] text-white/20">
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> Role: Health Coach</span>
            <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> Data: Adherence gaps, inactivity alerts, outreach tasks</span>
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Actions: Complete, escalate to counselor, add notes</span>
          </div>
        </div>

        {(data?.tasks || []).length > 0 ? (
          <div className="space-y-2">{data.tasks.map(t => <TaskCard key={t.id} task={t} role="health_coach" onAction={handleAction} highlightedId={highlightedId} />)}</div>
        ) : (
          <div className="text-center py-12 text-adm-tertiary text-xs">No execution tasks pending. All clear.</div>
        )}
      </div>
    </AdminLayout>
  );
}
